import {
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  Min,
  Max,
  MaxLength,
  IsUUID,
  IsEnum,
  IsArray,
} from 'class-validator'
import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger'
import { Transform } from 'class-transformer'
import {
  PedagogicContentState,
  ContentVisibility,
} from '../../../enums/content.enum'
import {
  MAX_CHAPTER_TITLE_LENGTH,
  MAX_CHAPTER_DESCRIPTION_LENGTH,
  MIN_ESTIMATED_HOURS,
  MAX_ESTIMATED_HOURS,
} from '../../../constants/chapter.constants'

/**
 * Helper to convert empty string to undefined
 */
const toStringOrUndefined = ({ value }: { value: any }) => {
  if (value === '' || value === null) return undefined
  return value
}

/**
 * Helper to convert string to number
 */
const toNumber = ({ value }: { value: any }) => {
  if (value === '' || value === null || value === undefined) return undefined
  const num = Number(value)
  return isNaN(num) ? value : num
}

/**
 * DTO for creating a new chapter
 */
export class CreateChapterDto {
  @ApiPropertyOptional({
    description: 'ID de la matière',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  subjectExternalId?: string

  @ApiPropertyOptional({
    description: 'ID du niveau pédagogique',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsUUID('4')
  levelExternalId?: string

  @IsOptional()
  @IsUUID('4')
  teachingId?: string

  @ApiPropertyOptional({
    description: 'IDs des enseignements à associer (optionnel)',
    type: [String],
    example: ['550e8400-e29b-41d4-a716-446655440000'],
  })
  @IsOptional()
  @IsUUID('4', { each: true })
  teachingIds?: string[]

  @ApiProperty({
    description: 'Titre du chapitre',
    example: 'Chapitre 1 - Les bases des mathématiques',
    maxLength: MAX_CHAPTER_TITLE_LENGTH,
    type: 'string',
  })
  @IsNotEmpty({ message: 'Le titre du chapitre est requis' })
  @IsString()
  @MaxLength(MAX_CHAPTER_TITLE_LENGTH, {
    message: `Le titre ne peut pas dépasser ${MAX_CHAPTER_TITLE_LENGTH} caractères`,
  })
  title!: string

  @ApiPropertyOptional({
    description: 'Description détaillée du chapitre',
    example:
      'Ce chapitre introduit les concepts fondamentaux des mathématiques',
    maxLength: MAX_CHAPTER_DESCRIPTION_LENGTH,
    type: 'string',
  })
  @IsOptional()
  @IsString()
  @MaxLength(MAX_CHAPTER_DESCRIPTION_LENGTH, {
    message: `La description ne peut pas dépasser ${MAX_CHAPTER_DESCRIPTION_LENGTH} caractères`,
  })
  description?: string

  @ApiPropertyOptional({
    description: "Nombre d'heures estimées pour le chapitre",
    example: 10.5,
    minimum: MIN_ESTIMATED_HOURS,
    maximum: MAX_ESTIMATED_HOURS,
    type: Number,
  })
  @IsOptional()
  @Transform(toNumber)
  @IsNumber({}, { message: "Le nombre d'heures doit être un nombre" })
  @Min(MIN_ESTIMATED_HOURS, {
    message: `Le nombre d'heures doit être au moins ${MIN_ESTIMATED_HOURS}`,
  })
  @Max(MAX_ESTIMATED_HOURS, {
    message: `Le nombre d'heures ne peut pas dépasser ${MAX_ESTIMATED_HOURS}`,
  })
  estimatedHours?: number

  @ApiProperty({
    description: 'ID du statut du chapitre',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsNotEmpty({ message: 'Le statut du chapitre est requis' })
  @IsUUID('4', { message: "L'ID du statut doit être un UUID valide" })
  statusId!: string

  @ApiPropertyOptional({
    description: "ID de l'établissement (optionnel)",
    example: '660e8400-e29b-41d4-a716-446655440001',
    type: 'string',
  })
  @IsOptional()
  @Transform(toStringOrUndefined)
  @IsUUID('4', { message: "L'ID de l'établissement doit être un UUID valide" })
  establishmentId?: string

  @ApiPropertyOptional({
    description: 'État du chapitre',
    enum: PedagogicContentState,
    default: PedagogicContentState.ACTIVE,
  })
  @IsOptional()
  @IsEnum(PedagogicContentState)
  state?: PedagogicContentState

  @ApiPropertyOptional({
    description: 'Visibilité du chapitre',
    enum: ContentVisibility,
    default: ContentVisibility.PRIVATE,
  })
  @IsOptional()
  @IsEnum(ContentVisibility)
  visibility?: ContentVisibility

  @ApiPropertyOptional({
    description: 'IDs des concepts du programme à associer au chapitre',
    type: [String],
    example: ['550e8400-e29b-41d4-a716-446655440000'],
  })
  @IsOptional()
  @IsArray()
  @IsUUID('4', { each: true })
  programConceptIds?: string[]
}
